import React, { useEffect, useMemo, useRef, useState } from 'react';
import BRAND_CONFIG from './utils/brandConfig.js';
import {
  generateSignatureHTML,
  copySignatureAsHtmlImage,
  copySignatureAsImage,
  downloadSignatureAsImage,
  fetchSignatureAssets,
} from './utils/signatureGenerator.js';
import './styles/App.css';

const INITIAL_DATA = {
  name: '',
  title: '',
  cell: '',
  main: BRAND_CONFIG.company.mainPhone,
  email: '',
  linkedin: '',
  badges: ['services', 'staffing', 'advisory'],
  tagline: BRAND_CONFIG.taglines[0],
};

function App() {
  const [data, setData] = useState(INITIAL_DATA);
  const [assets, setAssets] = useState(null);
  const [copyState, setCopyState] = useState('idle');
  const [toastMessage, setToastMessage] = useState('');
  const toastTimer = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    fetchSignatureAssets()
      .then(setAssets)
      .catch((err) => {
        console.error('Failed to load signature assets:', err);
      });
  }, []);

  const previewHtml = useMemo(() => generateSignatureHTML(data), [data]);


  const update = (field, value) => setData((prev) => ({ ...prev, [field]: value }));

  const toggleBadge = (id) => {
    setData((prev) => ({
      ...prev,
      badges: prev.badges.includes(id)
        ? prev.badges.filter((b) => b !== id)
        : [...prev.badges, id],
    }));
  };

  const showToast = (msg) => {
    setToastMessage(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMessage(''), 2800);
  };

  const handleCopy = async () => {
    if (!previewRef.current || !hasContent) {
      showToast('Fill in your details first.');
      return;
    }
    try {
      await copySignatureAsHtmlImage(previewRef.current, { email: data.email });
      setCopyState('copied');
      showToast('Signature copied as HTML — paste it into your email client.');
      setTimeout(() => setCopyState('idle'), 2500);
    } catch (e) {
      console.error('Copy failed:', e);
      showToast('Copy failed. Try Copy as Image instead.');
    }
  };

  const hasContent = data.name || data.title || data.cell || data.main || data.email;
  const filenameSafeName = (data.name || 'three_link_signature').replace(/\s+/g, '_').replace(/[^A-Za-z0-9_\-]/g, '');

  const handleDownloadImage = async () => {
    if (!previewRef.current || !hasContent) {
      showToast('Fill in your details first.');
      return;
    }
    try {
      await downloadSignatureAsImage(previewRef.current, `${filenameSafeName}_signature.png`);
      showToast('Image downloaded.');
    } catch (e) {
      console.error('Image download failed:', e);
      showToast('Image download failed — see console.');
    }
  };

  const handleCopyImage = async () => {
    if (!previewRef.current || !hasContent) {
      showToast('Fill in your details first.');
      return;
    }
    try {
      await copySignatureAsImage(previewRef.current);
      showToast('Image copied to clipboard.');
    } catch (e) {
      console.error('Image copy failed:', e);
      showToast('Image copy failed — your browser may not support image clipboard.');
    }
  };

  const handleReset = () => setData(INITIAL_DATA);

  return (
    <div className="page">
      <header className="page-header">
        <div className="logo-mark" aria-hidden="true">
          <img src="/assets/brand/logos/chainlink_orange.png" alt="" />
        </div>
        <div>
          <h1>Email Signature Generator</h1>
          <p>Complete the fields below to generate your 3LINK email signature</p>
        </div>
      </header>

      <main className="layout">
        <section className="card">
          <h2 className="card-title"><span className="dot" />Your Information</h2>

          <div className="form-group">
            <label htmlFor="f-name">Full Name <span className="req">*</span></label>
            <input
              id="f-name"
              type="text"
              placeholder="First Name Last Name"
              value={data.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="f-title">Job Title <span className="req">*</span></label>
            <input
              id="f-title"
              type="text"
              placeholder="Job Title goes here"
              value={data.title}
              onChange={(e) => update('title', e.target.value)}
            />
          </div>

          <div className="row-2">
            <div className="form-group">
              <label htmlFor="f-cell">Cell Phone <span className="muted">(optional)</span></label>
              <input
                id="f-cell"
                type="tel"
                placeholder="###.###.####"
                value={data.cell}
                onChange={(e) => update('cell', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-main">Main Phone <span className="muted">(optional)</span></label>
              <input
                id="f-main"
                type="tel"
                placeholder="844.443.5465"
                value={data.main}
                onChange={(e) => update('main', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="f-email">Email Address <span className="muted">(optional)</span></label>
            <input
              id="f-email"
              type="email"
              placeholder="firstname.lastname@threelink.com"
              value={data.email}
              onChange={(e) => update('email', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="f-linkedin">LinkedIn URL <span className="muted">(optional)</span></label>
            <input
              id="f-linkedin"
              type="url"
              placeholder="https://linkedin.com/in/your-profile"
              value={data.linkedin}
              onChange={(e) => update('linkedin', e.target.value)}
            />
          </div>
        </section>

        <section className="card">
          <h2 className="card-title"><span className="dot" />Workday Partner Badges</h2>
          <p className="card-subtitle">Select the partner designations to include</p>

          <div className="badge-group">
            {BRAND_CONFIG.partners.map((p) => {
              const checked = data.badges.includes(p.id);
              return (
                <label
                  key={p.id}
                  className={`badge-check ${checked ? 'checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleBadge(p.id)}
                  />
                  <span>
                    <strong>{p.label}</strong>
                    <span className="badge-sub">Workday {p.label}</span>
                  </span>
                </label>
              );
            })}
          </div>

          <h2 className="card-title" style={{ marginTop: 28 }}><span className="dot" />Tagline</h2>
          <div className="form-group">
            <select value={data.tagline} disabled aria-disabled="true">
              <option value={BRAND_CONFIG.taglines[0]}>{BRAND_CONFIG.taglines[0]}</option>
            </select>
            <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>Tagline is locked to the standard 3LINK tagline.</p>
          </div>

          <div className="btn-row">
            <button className="btn btn-primary" onClick={handleCopyImage}>
              Copy as Image
            </button>
            <button className="btn btn-secondary" onClick={handleCopy}>
              {copyState === 'copied' ? '✓ Copied!' : 'Copy as HTML'}
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadImage}>
              Download PNG
            </button>
            <button className="btn btn-ghost" onClick={handleReset}>Reset</button>
          </div>
          <p className="btn-help">
            Both <em>Copy as Image</em> and <em>Copy as HTML</em> render pixel-perfectly in Outlook desktop dark mode (the signature is captured as an image either way). The HTML option additionally wraps the image in a click-through to threelink.com.
          </p>
        </section>

        <section className="card preview-card">
          <div className="preview-label">
            <span><span className="dot" />Live Preview</span>
            <span className="live-badge">● LIVE</span>
          </div>
          <div className="preview-wrapper">
            {hasContent ? (
              <div ref={previewRef} dangerouslySetInnerHTML={{ __html: previewHtml }} />
            ) : (
              <p className="preview-placeholder">Fill in your details above to see your signature preview.</p>
            )}
          </div>

          <div className="instructions">
            <strong>How to install your signature</strong>
            <p><b>Outlook (desktop):</b> File → Options → Mail → Signatures → New → paste into the editor.</p>
            <p><b>Outlook (web) / Gmail / Apple Mail:</b> Open signature settings, then paste directly into the rich-text editor.</p>
            <p className="instructions-tip">Both output options paste the signature as an image (white background guaranteed in dark mode). If the pasted image is too large, click it in the compose window and drag a corner handle to resize. To use this in Outlook's Signature settings, click <em>Picture</em> when adding/editing your signature and select the downloaded PNG.</p>
          </div>
        </section>
      </main>

      {toastMessage && <div className="toast">{toastMessage}</div>}
    </div>
  );
}

export default App;
